import { Button, FullScreenLoading, Icon, SvgIcon } from '@civilgrid/ui/components';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowData,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { debounce, isEmpty, toNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { useSnapshot } from 'valtio';
import {
  exportIntelligenceSummariesToExcel as apiExportToExcel,
  listIntelligenceSummaries as apiListSummaries,
  listSummariesById as apiListSummariesById,
} from '~/api/projects2';
import { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import { createExcelSheetDef, downloadExcelFileBuffer, type ExcelSheetDef } from '~/common/exportUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useQuery } from '~/common/routerUtils';
import { type IntelligenceSummary, type OrganizationDetail, PlanStatus, type TLoadedState } from '~/common/types';
import { MetricCard } from '~/components2/dashboard/widgets/MetricCard';
import { Notify } from '~/components2/Notify';
import { GlobalFilter, Pagination, TableV2 } from '~/components2/Table';
import { setConflictsSummaryViewPref } from '~/hooks/useConflictsSummaryPath';
import { useIsMounted } from '~/hooks/useIsMounted';
import { useNewConflictSummaryEnabled } from '~/hooks/useNewConflictSummaryEnabled';
import { useOrgFeatureFlag } from '~/hooks/useOrgFeatureFlag';
import store from '~/store';
import ProtectedRouteLayout from '../common/ProtectedRouteLayout';
import Card, { Label } from './Card';
import { toMiles } from './columns';
import { isMissingDates, useSummaryConfig } from './common';
import StackedBarChart from './StackedBarChart';
import { useChartData } from './useChartData';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateValue?: (rowId: string, updates: { columnId: string; level: 'project' | 'summary'; value: any }[]) => void;

    addToPoll?: (summaryId: string) => void;
  }
}

export default function ConflictsSummary() {
  useOrgFeatureFlag({ flag: OrgFeatureFlag.ConflictsSummary, redirectTo: '/home' });
  const newConflictSummaryEnabled = useNewConflictSummaryEnabled();
  const [data, setData] = useState<IntelligenceSummary[]>([]);
  const [summariesToPoll, setSummariesToPoll] = useState<string[]>([]);
  const { currentOrganizationId, currentOrganizationDetail } = useSnapshot(store) as typeof store;
  const query = useQuery();
  const isMounted = useIsMounted();

  const conflictsSummaryConfig = (currentOrganizationDetail as TLoadedState & OrganizationDetail)
    .conflictsSummaryConfig;

  const config = useSummaryConfig({ conflictsSummaryConfig });

  const { mutate: submitExport } = useMutation({
    mutationKey: ['conflicts-summary-export-to-excel', { currentOrganizationId }],
    mutationFn: ({ sheetDefs }: { sheetDefs: ExcelSheetDef[]; rowCount: number; startedAt: number }) =>
      apiExportToExcel(currentOrganizationId, sheetDefs).then((buffer) =>
        downloadExcelFileBuffer(
          buffer,
          `Conflicts-Summary_${(currentOrganizationDetail as any)?.name}_${new Date().toLocaleString()}`,
        ),
      ),
    onSuccess: (_data, variables) => {
      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryExportSucceeded, {
        row_count: variables.rowCount,
        duration_ms: Date.now() - variables.startedAt,
      });
    },
    onError: (error, variables) => {
      Notify.error({
        message: 'An error occurred while exporting the conflicts summary. Please contact support.',
      });
      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryExportFailed, {
        row_count: variables.rowCount,
        error_message: error instanceof Error ? error.message : 'unknown',
      });
    },
  });

  const summaries = useAsync(async () => {
    if (!currentOrganizationId) return [];
    const res = await apiListSummaries(currentOrganizationId);
    setData([...res]);
  }, [currentOrganizationId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const projectYears = useMemo(() => {
    const years: Set<string> = new Set();
    data.forEach((s) => {
      if (s.project.constructionYear) {
        years.add(s.project.constructionYear.toString());
      }
    });

    return Array.from(years).sort((a, b) => a.localeCompare(b));
  }, [data, query.get('year')]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const controller = new AbortController();

    const int = setInterval(async () => {
      if (!isEmpty(summariesToPoll)) {
        const summaryMap: { [key: string]: IntelligenceSummary } = {};

        const sums = await apiListSummariesById(currentOrganizationId, summariesToPoll, controller);

        sums.forEach((s) => {
          if (s.computeStatus !== 'processing') {
            summaryMap[s.id] = s;
          }
        });

        if (isMounted() && !isEmpty(summaryMap)) {
          setData((prev) =>
            prev.map((s) => {
              if (summaryMap[s.id]) {
                return summaryMap[s.id];
              }

              return s;
            }),
          );

          const toRemove = Object.keys(summaryMap);
          setSummariesToPoll((prev) => prev.filter((id) => !toRemove.includes(id)));
        }
      }
    }, 5_000);

    return () => {
      clearInterval(int);
      controller.abort();
    };
  }, [summariesToPoll]);

  const isLoading = summaries.loading;

  const [sorting, setSorting] = useState<SortingState>([{ id: 'percentConflict', desc: true }]);

  const table = useReactTable({
    data,
    columns: config.columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: (row) => row.project.id,
    state: { sorting },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (next[0]) {
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummarySortChanged, {
            column_id: next[0].id,
            direction: next[0].desc ? 'desc' : 'asc',
          });
        }
        return next;
      });
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 100 },
    },
    meta: {
      updateValue: (rowId, updates) => {
        setData((prev) =>
          prev.map((summary) => {
            if (summary.project.id === rowId) {
              const newSummary = { ...summary };

              updates.forEach((u) => {
                if (u.level === 'project') {
                  newSummary.project = {
                    ...newSummary.project,
                    [u.columnId]: u.value,
                  };
                } else {
                  (newSummary as any)[u.columnId] = u.value;
                }
              });

              return newSummary;
            }

            return summary;
          }),
        );
      },
      addToPoll: (summaryId) => {
        setSummariesToPoll((prev) => Array.from(new Set([...prev, summaryId])));
      },
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const v = toNumber(query.get('year'));
    const c = table.getColumn('constructionYear');
    if (v) {
      c?.setFilterValue([v, v]);
    } else {
      c?.setFilterValue('');
    }
  }, [query.get('year')]);

  const { chartData: yearData, segments: yearSegments } = useChartData(
    table.getFilteredRowModel().rows,
    'constructionYear',
  );

  const { chartData: regionData, segments: regionSegments } = useChartData(table.getFilteredRowModel().rows, 'region');

  const { chartData: divisionData, segments: divisionSegments } = useChartData(
    table.getFilteredRowModel().rows,
    'division',
  );

  const getCount = (summary: IntelligenceSummary, field: keyof IntelligenceSummary): number => {
    if (isMissingDates(summary)) {
      return 0;
    }

    return (summary[field] as number) || 0;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const count = useMemo(() => {
    const rows = table.getFilteredRowModel().rows;
    const total = rows.length;
    let needsAttn = 0;
    let planned = 0;
    let pending = 0;
    let conflictArea = 0;
    let gasServiceCount = 0;
    let gasDistributionLength = 0;

    rows.forEach((row) => {
      const s = row.original;
      const { planStatus } = s.project;
      if (planStatus === PlanStatus.NeedsAttention) {
        needsAttn++;
      } else if (planStatus === PlanStatus.Scheduled) {
        planned++;
      } else if (planStatus === PlanStatus.Pending) {
        pending++;
      }

      conflictArea += getCount(s, 'conflictArea');
      gasServiceCount += getCount(s, 'gasServiceCount');
      gasDistributionLength += getCount(s, 'gasDistributionLength');
    });

    return {
      total,
      needsAttn,
      planned,
      conflictArea,
      gasServiceCount,
      gasDistributionLength: toMiles(gasDistributionLength),
      pending,
    };
  }, [table.getFilteredRowModel().rows]);

  const trackSearch = useMemo(
    () =>
      debounce((value: string) => {
        capturePosthogEvent(POSTHOG_EVENTS.conflictsSummarySearchChanged, {
          has_query: value.length > 0,
          query_length: value.length,
        });
      }, 500),
    [],
  );

  const onExport = async () => {
    const columns = table.getAllColumns();
    const { rows } = table.getCoreRowModel();
    const yearFilter = query.get('year');

    capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryExportClicked, {
      row_count: rows.length,
      year_filter: yearFilter ?? null,
    });

    const createSheetByYear = (year: string) => {
      return createExcelSheetDef({
        name: `${year}`,
        columns,
        rows: rows.filter((r) => r.original.project.constructionYear?.toString?.() === year),
      });
    };

    const allItems = createExcelSheetDef({
      name: 'All',
      columns,
      rows,
    });

    const itemsByYear = projectYears.map(createSheetByYear);
    const sheetDefs = [allItems, ...itemsByYear];
    submitExport({ sheetDefs, rowCount: rows.length, startedAt: Date.now() });
  };

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <ProtectedRouteLayout includeOrgProjectSwitcher={true} includeSearch={false} fluidHeight>
      <div className="space-y-1 p-4" data-testid="conflicts-summary-page">
        <div className="flex items-center justify-between" data-testid="conflicts-summary-header">
          <h1 className="text-xl font-semibold text-blue-80">Conflicts Summary</h1>
          <div className="flex items-center gap-2">
            {newConflictSummaryEnabled && (
              <Button asChild variant="secondary" data-testid="conflicts-summary-try-new-link">
                <Link
                  to="/conflictsSummaryV2"
                  onClick={() => {
                    setConflictsSummaryViewPref('new');
                    capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryV2TryNewClicked, {});
                  }}
                >
                  <Icon icon="update" size={14} /> New View
                </Link>
              </Button>
            )}
            <Button asChild variant="secondary" data-testid="conflicts-summary-data-refresh-status-button">
              <Link
                to="/conflictsSummary/dataRefreshStatus"
                onClick={() =>
                  capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryDataRefreshStatusClicked, {
                    organization_id: currentOrganizationId,
                  })
                }
              >
                <SvgIcon icon="database" size={14} alt="" /> Data Refresh Status
              </Link>
            </Button>
          </div>
        </div>
        <div className="flex w-full flex-col justify-between space-x-4 space-y-1 pt-1">
          <Card isLoading={isLoading} className="col-span-2 flex flex-col">
            <Label>Overview</Label>

            <div className={classNames('grid grid-cols-3 gap-2', config.showGasInfo && 'lg:grid-cols-5')}>
              <MetricCard
                title={config.statusLabels.needsAttention}
                value={count.needsAttn.toLocaleString()}
                totalValue={count.total}
                tooltip={<div>Number of projects that are pending review.</div>}
                data-testid="metric-card-needs-review"
              />

              <MetricCard
                title={config.statusLabels.scheduled}
                value={count.planned.toLocaleString()}
                totalValue={count.total}
                tooltip={<div>Number of projects that have been reviewed.</div>}
                data-testid="metric-card-cleared"
              />

              <MetricCard
                title="Total Conflict Area (sq. ft.)"
                value={Math.round(count.conflictArea).toLocaleString()}
                data-testid="metric-card-total-conflict-area"
              />

              {config.showGasInfo && (
                <>
                  <MetricCard
                    title="Total Gas Service Count"
                    value={count.gasServiceCount.toLocaleString()}
                    data-testid="metric-card-gas-service-count"
                  />

                  <MetricCard
                    title="Total Gas Distribution Length (mi.)"
                    value={count.gasDistributionLength.toLocaleString()}
                    data-testid="metric-card-gas-distribution-length"
                  />
                </>
              )}
            </div>
          </Card>

          {config.showCharts && (
            <div
              className="flex h-[280px] items-center justify-center space-x-2"
              data-testid="conflicts-summary-charts"
            >
              <StackedBarChart
                data={yearData}
                segments={yearSegments}
                title={'Yearly Breakdown'}
                data-testid="stacked-bar-chart-yearly"
              />

              <StackedBarChart
                data={regionData}
                segments={regionSegments}
                title={'Regional Breakdown'}
                data-testid="stacked-bar-chart-regional"
              />

              <StackedBarChart
                data={divisionData}
                segments={divisionSegments}
                title={'Divisional Breakdown'}
                data-testid="stacked-bar-chart-divisional"
              />
            </div>
          )}
        </div>
        <div className="relative flex grow flex-col space-y-2 p-2">
          <TableV2
            id="conflicts-summary-table"
            table={table}
            topSection={
              <div className="py-2" data-testid="conflicts-summary-toolbar">
                <div className="flex items-center justify-start gap-4" data-testid="conflicts-summary-year-filter">
                  <Label>Plan</Label>
                  <div className="flex gap-2">
                    <YearLink isActive={!query.get('year')} previousYear={query.get('year')} />
                    {projectYears.map((year) => {
                      return (
                        <YearLink
                          key={`year-filter-${year}`}
                          year={year}
                          isActive={query.get('year') === year}
                          previousYear={query.get('year')}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-end justify-between">
                  <Pagination table={table} data-testid="conflicts-summary-pagination" />

                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant="secondary"
                      className="mt-1"
                      onClick={onExport}
                      data-testid="conflicts-summary-export-button"
                    >
                      Export to Excel
                    </Button>

                    <div className="col-span-2">
                      <GlobalFilter
                        table={table}
                        placeholder="Search..."
                        label=""
                        className="h-[30px]"
                        data-testid="conflicts-summary-search-input"
                        onFilterChange={trackSearch}
                      />
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </ProtectedRouteLayout>
  );
}

const YearLink = ({
  year,
  isActive,
  previousYear,
}: {
  year?: string;
  isActive?: boolean;
  previousYear?: string | null;
}) => {
  let to = '/conflictsSummary';

  if (year) {
    to += `?year=${year}`;
  }

  const onClick = () => {
    if (isActive) return;
    capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryYearFilterChanged, {
      year: year ? Number(year) : null,
      previous_year: previousYear ? Number(previousYear) : null,
    });
  };

  return (
    <Link
      to={to}
      onClick={onClick}
      className={classNames(
        'rounded-lg px-2 py-0.5 border transition-colors cursor-pointer text-sm',
        isActive ? 'bg-blue-10 border-blue-50' : 'bg-white border-grey-30 hover:bg-grey-10 hover:shadow-md',
      )}
      data-testid={`plan-year-filter-${year ?? 'all'}`}
      data-active={isActive ? 'true' : 'false'}
    >
      {year || 'All'}
    </Link>
  );
};
